/**
 * AuthContext — thin React context adapter over the Zustand authStore.
 *
 * All business logic lives in src/store/authStore.ts.
 * This file exists purely to keep backward compatibility for any component
 * that still calls `useAuth()` via context, and to trigger `initialize()`
 * once on app mount.
 */
import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuthStore } from '@/store/authStore';
import type { VerificationChannel } from '@/lib/api/auth';
import type { User, LoginRequest, MemberRegisterRequest, VenueRegisterRequest } from '@/types/api';

// ─── Public interface (unchanged from original) ──────────────────────────────

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  sessionReady: boolean;
  error: string | null;
  pendingVerification: { userId: string; phone: string } | null;
  pendingEmailVerification: boolean;
  login: (credentials: LoginRequest) => Promise<User>;
  registerMember: (data: MemberRegisterRequest) => Promise<{
    requiresVerification: boolean;
    requiresEmailVerification: boolean;
    userId?: string;
  }>;
  registerVenue: (data: VenueRegisterRequest) => Promise<{ requiresVerification: boolean; userId?: string; requiresEmailVerification?: boolean }>;
  verifyPhone: (code: string) => Promise<void>;
  resendVerificationCode: (channel?: VerificationChannel) => Promise<VerificationChannel>;
  resendEmailVerification: () => Promise<void>;
  checkEmailVerified: () => Promise<boolean>;
  clearPendingVerification: () => void;
  logout: () => void;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  applyMemberAccountTypeId: (accountTypeId: number) => void;
  applyContactChange: (change: {
    email?: string;
    phone?: string;
    emailChanged?: boolean;
    phoneChanged?: boolean;
  }) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const initialize = useAuthStore((s) => s.initialize);

  // Run once on mount — restores session from persisted token
  useEffect(() => {
    void initialize();
  }, [initialize]);

  return <AuthContext.Provider value={useAuthStore.getState()}>{children}</AuthContext.Provider>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * useAuth — reads live auth state directly from the Zustand store.
 * Components using this hook will re-render on any auth state change.
 */
export function useAuth(): AuthContextType {
  // Read directly from store for reactivity (context value is static snapshot)
  const store = useAuthStore();
  return {
    user: store.user,
    isAuthenticated: !!store.user,
    isLoading: store.isLoading,
    sessionReady: store.sessionReady,
    error: store.error,
    pendingVerification: store.pendingVerification,
    pendingEmailVerification: store.pendingEmailVerification,
    login: store.login,
    registerMember: store.registerMember,
    registerVenue: store.registerVenue,
    verifyPhone: store.verifyPhone,
    resendVerificationCode: store.resendVerificationCode,
    resendEmailVerification: store.resendEmailVerification,
    checkEmailVerified: store.checkEmailVerified,
    clearPendingVerification: store.clearPendingVerification,
    logout: store.logout,
    clearError: store.clearError,
    refreshUser: store.refreshUser,
    applyMemberAccountTypeId: store.applyMemberAccountTypeId,
    applyContactChange: store.applyContactChange,
  };
}

export default AuthContext;
